/**
 * <SelectField> — un champ qui ouvre sa liste de choix (molecule).
 *
 * Le sélecteur des formulaires du compte : le champ montre le choix en cours et un chevron, le
 * toucher ouvre une feuille (`<Modal>`) qui porte la liste (`<Autocomplete>`). Même patron que
 * `<DatePicker>` : un champ pressable et son sélecteur encapsulé, la valeur en props. Quelques
 * réponses courtes qu'on tape d'un geste prennent une rangée de pilules (`<ChoiceChip>`) ; ce
 * champ sert dans un formulaire long, quand les options sont nombreuses (un sport, un pays) ou
 * longues (une phrase), et qu'on veut lire la réponse en cours sans dérouler la liste.
 *
 * Choix unique : toucher une option la choisit et referme la feuille. Choix multiple : les choix
 * s'affichent en chips dans le champ, et la feuille se referme par « Terminer ». La recherche
 * n'apparaît qu'au-delà de quelques options.
 *
 * L'ouverture de la feuille est un état d'interface, donc local ; `defaultOpen` l'ouvre d'office
 * pour une story.
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { TEXT_INPUT_HEIGHT } from "../../atoms/TextInput/TextInput"
import { Text } from "../../atoms/typography/Text"
import { Autocomplete } from "../Autocomplete"
import { Modal } from "../Modal"
import { type UISelectOption } from "../../../ports/primary/ui/types"

export type { UISelectOption }

/** En dessous, la liste se lit d'un coup d'œil : pas de champ de recherche. */
const SEARCH_FROM = 15

export type SelectFieldProps<T> = {
    label?: string
    /** Ce que le champ affiche tant que rien n'est choisi. Défaut « Choisir ». */
    placeholder?: string
    items: T[]
    getItemId: (item: T) => string | number
    getItemLabel: (item: T) => string
    /** Le choix en cours. Un seul id au plus en choix unique. */
    selectedIds: Array<string | number>
    /** La sélection après le geste : `[id]` en choix unique, la liste à jour en choix multiple. */
    onChange: (ids: Array<string | number>) => void
    /** Plusieurs choix à la fois. Défaut `false`. */
    multiple?: boolean
    searchPlaceholder?: string
    /** Message d'erreur sous le champ : le contour passe au rouge. */
    error?: string
    disabled?: boolean
    /** Ouvre la feuille au premier rendu (stories). */
    defaultOpen?: boolean
    testID?: string
}

export function SelectField<T>(props: SelectFieldProps<T>) {
    const {
        label,
        placeholder = "Choisir",
        items,
        getItemId,
        getItemLabel,
        selectedIds,
        onChange,
        multiple = false,
        searchPlaceholder,
        error,
        disabled = false,
        defaultOpen = false,
        testID,
    } = props

    const [open, setOpen] = useState(defaultOpen)

    const selectedKeys = new Set(selectedIds.map(String))
    const selectedItems = items.filter((item) => selectedKeys.has(String(getItemId(item))))
    const empty = selectedItems.length === 0
    const borderKey: SemanticColorKey = error != null && !disabled ? "danger" : "border"
    const summary = empty ? placeholder : selectedItems.map(getItemLabel).join(", ")

    const toggle = (id: string | number) => {
        if (!multiple) {
            onChange([id])
            setOpen(false)
            return
        }
        const key = String(id)
        onChange(selectedKeys.has(key) ? selectedIds.filter((x) => String(x) !== key) : [...selectedIds, id])
    }

    return (
        <Stack gap="1" testID={testID}>
            {label != null && (
                <Text size="text-base" weight="bold" color="primary">
                    {label}
                </Text>
            )}

            <Pressable
                disabled={disabled}
                onPress={() => setOpen(true)}
                accessibilityRole="button"
                accessibilityLabel={label != null ? `${label} : ${summary}` : summary}
                accessibilityState={{ disabled }}
                style={({ pressed }) => [styles.field(borderKey, disabled), pressed ? styles.pressed : undefined]}
            >
                <Row gap="2" align="center">
                    {multiple && !empty ? (
                        <View style={styles.chips}>
                            {selectedItems.map((item) => (
                                <Badge key={String(getItemId(item))} text={getItemLabel(item)} variant="primary" />
                            ))}
                        </View>
                    ) : (
                        <Text color={empty ? "text-muted" : "text-strong"} style={styles.value}>
                            {summary}
                        </Text>
                    )}
                    <Icon name="chevron-down" size={16} color="text-muted" />
                </Row>
            </Pressable>

            {error != null && (
                <Text size="text-sm" color="text-danger">
                    {error}
                </Text>
            )}

            <Modal
                visible={open}
                onClose={() => setOpen(false)}
                title={label ?? placeholder}
                // La tête d'une étape : un intitulé long passe à la ligne au lieu de buter sur la croix.
                steps={{ step: 1, total: 1 }}
                sizeToContent
                footer={multiple ? <Button label="Terminer" fullWidth onPress={() => setOpen(false)} /> : undefined}
            >
                <Autocomplete
                    selection={multiple ? "multiple" : "single"}
                    searchable={items.length >= SEARCH_FROM}
                    items={items}
                    getItemId={getItemId}
                    getItemLabel={getItemLabel}
                    selectedIds={selectedIds}
                    onToggle={toggle}
                    searchPlaceholder={searchPlaceholder}
                />
            </Modal>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La boîte de `<TextInput>` : même contour, même rayon, même hauteur de ligne. */
    field: (borderKey: SemanticColorKey, disabled: boolean) => ({
        borderWidth: 1,
        borderColor: theme.colors[borderKey],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors[disabled ? "surface-subtle" : "surface-raised"],
        paddingHorizontal: theme.spacing["3"],
        paddingVertical: theme.spacing["2"],
        minHeight: TEXT_INPUT_HEIGHT,
        justifyContent: "center",
        opacity: disabled ? 0.5 : 1,
    }),
    pressed: { opacity: 0.6 },
    value: { flexShrink: 1, flexGrow: 1 },
    chips: {
        flexDirection: "row",
        flexWrap: "wrap",
        flexShrink: 1,
        flexGrow: 1,
        gap: theme.spacing["1"],
    },
}))
